import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Pencil } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/components/ui/use-toast"

interface RenameOrganizationDialogProps {
  organizationId: string
  currentName: string
  onRename: () => void
}

export function RenameOrganizationDialog({ organizationId, currentName, onRename }: RenameOrganizationDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [organizationName, setOrganizationName] = useState(currentName)
  const [isLoading, setIsLoading] = useState(false)

  const handleRenameOrganization = async () => {
    if (!organizationName.trim()) return

    setIsLoading(true)
    try {
      const { error } = await supabase
        .from("organizations")
        .update({ name: organizationName })
        .eq("id", organizationId)

      if (error) throw error

      toast({
        title: t("common.organizations.renamed"),
        description: t("common.organizations.renamedDescription"),
      })
      setOpen(false)
      onRename()
    } catch (error) {
      console.error("Error renaming organization:", error)
      toast({
        title: t("common.error"),
        description: t("common.organizations.errorRenaming"),
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <Pencil className="h-4 w-4" />
          <span className="sr-only">{t("common.organizations.rename")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.organizations.rename")}</DialogTitle>
          <DialogDescription>
            {t("common.organizations.renameDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t("common.organizations.name")}</Label>
            <Input
              id="name"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder={t("common.organizations.namePlaceholder")}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="submit"
            onClick={handleRenameOrganization}
            disabled={isLoading}
          >
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}